import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ChevronDown, ChevronRight, Loader2, Plus, Smartphone, Truck, Warehouse } from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { RequestError } from "./request-error"
import { RONDA_STATUS, createRonda, listRondaLocations, listRondas, type RondaListItem, type RondaLocation, type RondaMode } from "./ronda-api"
import { cn } from "@/lib/utils"

const when = (iso: string) => new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })

/** Quantas rondas do histórico se mostram de cada vez (aplicadas, fechadas). */
const PAGE = 10
/** Secções do histórico abertas, por pessoa e dispositivo (só conveniência: sem storage, ficam recolhidas). */
const EXPANDED_KEY = "maisenergia.rondas.expanded"

type GroupKey = "abertas" | "aplicadas" | "fechadas"

function readExpanded(): Partial<Record<GroupKey, boolean>> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_KEY)
    const v = raw ? (JSON.parse(raw) as unknown) : null
    return v && typeof v === "object" ? (v as Partial<Record<GroupKey, boolean>>) : {}
  } catch {
    return {}
  }
}

function writeExpanded(v: Partial<Record<GroupKey, boolean>>): void {
  try {
    window.localStorage.setItem(EXPANDED_KEY, JSON.stringify(v))
  } catch {
    // sem storage (janela privada, WebView sem dados): fica só nesta visita
  }
}

/**
 * Ronda de stock — passo 1 (mockup `docs/ronda.mockup.html`): começar uma
 * ronda numa localização (o armazém, uma prateleira, uma carrinha), o que se
 * vai contar e se se veem as quantidades do Odoo; e as rondas abertas (a
 * contar, por rever — as contagens de carrinha do telemóvel —, aplicadas com
 * linhas por recontar). Substitui os separadores Contagem e Conferência.
 * O formulário «Nova ronda» abre-se pelo botão do cabeçalho; a página começa
 * pelas rondas.
 */
export function RondasList({ shell }: { shell: AppShell }) {
  const [items, setItems] = useState<RondaListItem[] | null>(null)
  const [canManage, setCanManage] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  // O histórico (aplicadas, fechadas) cresce sem fim: recolhido por omissão, 10 de cada vez.
  const [expanded, setExpanded] = useState<Partial<Record<GroupKey, boolean>>>(readExpanded)
  const [shown, setShown] = useState<Partial<Record<GroupKey, number>>>({})
  const toggle = (key: GroupKey) =>
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      writeExpanded(next)
      return next
    })

  useEffect(() => {
    listRondas(shell)
      .then((d) => {
        setItems(d.items)
        setCanManage(d.can_manage)
      })
      .catch((e: Error) => setError(e.message))
  }, [shell])

  // As abertas são o trabalho do dia: sempre à vista. O resto é histórico.
  const groups: Array<{ key: GroupKey; title: string; history: boolean; match: (s: RondaListItem["status"]) => boolean }> = [
    { key: "abertas", title: "Rondas abertas", history: false, match: (s) => s === "aberta" || s === "por_rever" || s === "aplicada_parcial" },
    { key: "aplicadas", title: "Aplicadas", history: true, match: (s) => s === "aplicada" },
    {
      key: "fechadas",
      title: "Fechadas",
      history: true,
      match: (s) => s === "fechada_com_pendentes" || s === "abandonada" || s === "enviada" || s === "parcialmente_aplicada" || s === "cancelada",
    },
  ]

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Ronda de stock</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Contar o que está fisicamente numa localização. No fim, revê-se contra o Odoo e «Enviar» aplica — quem lê é quem aprova.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-primary px-4 text-[15px] font-semibold text-primary-foreground"
          >
            <Plus className="h-5 w-5" /> Nova ronda
          </button>
        )}
      </div>
      {creating && <NewRonda shell={shell} canManage={canManage} onCancel={() => setCreating(false)} />}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {items === null && !error && <p className="text-sm text-muted-foreground">A carregar…</p>}
      {items !== null && items.length === 0 && !creating && (
        <p className="text-sm text-muted-foreground">Ainda não há rondas. Carregue em «Nova ronda» para começar.</p>
      )}
      {groups.map((g) => {
        const list = (items ?? []).filter((i) => g.match(i.status))
        if (list.length === 0) return null
        if (!g.history) {
          return (
            <section key={g.key} className="space-y-2">
              <h2 className="text-base font-semibold">{g.title}</h2>
              {list.map((r) => (
                <RondaCard key={r.id} item={r} />
              ))}
            </section>
          )
        }
        const open = !!expanded[g.key]
        const limit = shown[g.key] ?? PAGE
        const visible = list.slice(0, limit)
        const rest = list.length - visible.length
        return (
          <section key={g.key} className="space-y-2">
            <h2>
              <button
                type="button"
                onClick={() => toggle(g.key)}
                aria-expanded={open}
                aria-controls={`rondas-${g.key}`}
                className="flex min-h-11 w-full items-center gap-2 rounded-lg text-left text-base font-semibold active:bg-muted/60"
              >
                <ChevronDown className={cn("h-5 w-5 shrink-0 text-muted-foreground transition-transform", !open && "-rotate-90")} />
                {g.title}
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">{list.length}</span>
              </button>
            </h2>
            {open && (
              <div id={`rondas-${g.key}`} className="space-y-2">
                {visible.map((r) => (
                  <RondaCard key={r.id} item={r} />
                ))}
                {rest > 0 && (
                  <button
                    type="button"
                    onClick={() => setShown((prev) => ({ ...prev, [g.key]: limit + PAGE }))}
                    className="flex h-11 w-full items-center justify-center rounded-xl border text-sm font-medium text-muted-foreground active:bg-muted"
                  >
                    Mostrar mais ({rest})
                  </button>
                )}
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

function RondaCard({ item }: { item: RondaListItem }) {
  const s = RONDA_STATUS[item.status]
  const Icon = item.source === "campo" ? Smartphone : item.van_id ? Truck : Warehouse
  const summary = [
    item.created_by_name,
    when(item.updated_at),
    item.products != null ? `${item.products} artigo(s)` : null,
    item.last_send ? `${item.last_send.applied} acerto(s)` : null,
    item.last_send?.assigned ? `${item.last_send.assigned} série(s) atribuída(s)` : null,
    item.to_recount ? `${item.to_recount} por recontar` : null,
    item.to_resolve ? `${item.to_resolve} por resolver` : null,
    item.sending ? "envio a meio" : null,
  ]
    .filter(Boolean)
    .join(" · ")
  return (
    <Link to={`/armazem/ronda/${item.id}`} className="block">
      <Card className="active:bg-muted/60">
        <CardContent className="flex items-center gap-3 p-3">
          <Icon className="h-6 w-6 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={item.status === "aberta" && item.in_review ? "warning" : s.variant} className="text-sm">
                {item.status === "aberta" && item.in_review ? "Em revisão" : s.label}
              </Badge>
              <span className="font-medium">{item.location_name ?? "Localização"}</span>
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {item.mode === "completa" ? "Tudo da localização" : "Só o que eu ler"} · {summary}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  )
}

/** Duas opções lado a lado (como no mockup). */
function Seg<K extends string>({ value, onChange, items }: { value: K; onChange: (k: K) => void; items: Array<{ key: K; label: string; disabled?: boolean }> }) {
  return (
    <div role="radiogroup" className="flex overflow-hidden rounded-xl border">
      {items.map((i) => (
        <button
          key={i.key}
          type="button"
          role="radio"
          aria-checked={value === i.key}
          disabled={i.disabled}
          onClick={() => onChange(i.key)}
          className={cn(
            "min-h-12 flex-1 px-2 text-[15px] disabled:opacity-40",
            value === i.key ? "bg-primary/10 font-semibold text-primary" : "text-foreground",
          )}
        >
          {i.label}
        </button>
      ))}
    </div>
  )
}

function NewRonda({ shell, canManage, onCancel }: { shell: AppShell; canManage: boolean; onCancel: () => void }) {
  const navigate = useNavigate()
  const [locations, setLocations] = useState<RondaLocation[] | null>(null)
  const [where, setWhere] = useState<string>("")
  const [mode, setMode] = useState<RondaMode>("parcial")
  // Por defeito: visível em «só o que eu ler», cega em «tudo» — muda-se em cada ronda.
  const [show, setShow] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existing, setExisting] = useState<string | null>(null)
  const showOdoo = show ?? mode === "parcial"

  useEffect(() => {
    listRondaLocations(shell)
      .then((d) => {
        setLocations(d.items)
        setWhere((w) => w || d.items[0]?.key || "")
      })
      .catch((e: Error) => setError(e.message))
  }, [shell])

  const start = async () => {
    const loc = locations?.find((l) => l.key === where)
    setBusy(true)
    setError(null)
    setExisting(null)
    try {
      const r = await createRonda(shell, { van_id: loc?.van_id ?? null, location: loc?.van_id ? null : (loc?.location ?? null), mode, blind: !showOdoo })
      navigate(`/armazem/ronda/${r.id}`)
    } catch (e) {
      setError((e as Error).message)
      const details = (e as RequestError).details as { check_id?: string } | undefined
      if (details?.check_id) setExisting(details.check_id)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div>
          <h2 className="text-lg font-semibold">Nova ronda</h2>
          <p className="text-sm text-muted-foreground">Contar o que está fisicamente numa localização.</p>
        </div>
        <label className="block">
          <span className="text-sm text-muted-foreground">Localização</span>
          <select
            value={where}
            onChange={(e) => setWhere(e.target.value)}
            disabled={!locations}
            className="mt-1 h-12 w-full rounded-xl border bg-background px-3 text-base"
            aria-label="Localização"
          >
            {(locations ?? []).map((l) => (
              <option key={l.key} value={l.key} disabled={l.van_id !== null && !l.location}>
                {l.van_id ? `Carrinha ${l.label}${l.location ? ` (${l.location})` : " — sem localização do Odoo"}` : l.label}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-muted-foreground">O armazém, uma prateleira ou uma carrinha (CAR-01…). Cada uma conta-se numa ronda própria.</span>
        </label>
        <div>
          <span className="text-sm text-muted-foreground">O que vai contar</span>
          <div className="mt-1">
            <Seg
              value={mode}
              onChange={setMode}
              items={[
                { key: "parcial", label: "Só o que eu ler" },
                { key: "completa", label: "Tudo da localização", disabled: !canManage },
              ]}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            <b>Só o que eu ler</b>: acerta os artigos lidos, os outros não mudam. <b>Tudo</b>: o que o Odoo tem e não for lido conta como zero (lista «vão a
            zero» no fim){canManage ? "." : " — só o responsável do armazém."}
          </p>
        </div>
        <div>
          <span className="text-sm text-muted-foreground">Mostrar as quantidades do Odoo enquanto conto?</span>
          <div className="mt-1">
            <Seg
              value={showOdoo ? "sim" : "nao"}
              onChange={(k) => setShow(k === "sim")}
              items={[
                { key: "nao", label: "Não (contagem cega)" },
                { key: "sim", label: "Sim" },
              ]}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Por defeito: visível em «Só o que eu ler»; cega em «Tudo da localização» (conta-se primeiro, compara-se no «Rever»).
          </p>
        </div>
        {error && (
          <p className="text-sm text-red-600">
            {error}{" "}
            {existing && (
              <Link to={`/armazem/ronda/${existing}`} className="font-medium underline">
                Abrir essa ronda
              </Link>
            )}
          </p>
        )}
        <button
          type="button"
          disabled={busy || !locations || !where}
          onClick={() => void start()}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} Começar
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="h-11 w-full rounded-xl text-base text-muted-foreground disabled:opacity-50"
        >
          Cancelar
        </button>
      </CardContent>
    </Card>
  )
}
